/**
 * useWalletConnection — the single source of truth for "is a player connected,
 * and which address acts on their behalf?". (The name predates web2; it stays so the
 * ~25 call sites keep one import.)
 *
 * Web2 (Suikamon 20): the identity is a Supabase account and its player key is the
 * account_id. Gate gameplay on `isConnected` / `address`, never on a wallet hook.
 * `hasSuiWallet` / `suiAddress` remain in the shape for the parked Sui-signing UI (withdraw,
 * transfer, balances) and are always false/null — no wallet provider is mounted.
 *
 * A third, weaker signal exists below IDENTITY: `isGuest`. A guest is a pre-identity
 * trial player — someone who adopted a free starter pet (free-to-play Phase 2) before
 * authenticating with any wallet/login. Their pet is keyed by an address-shaped sentinel
 * (`guest:<sessionId>`, see shared/domain/guestSession.ts), not a real chain address, and
 * never reaches an on-chain call. Call sites that should serve BOTH real identities and
 * guests gate on `isConnected || isGuest` and pass `actingAddress`. A connected identity
 * always wins over guest mode (once a guest logs in, Task 12's claim flow migrates the
 * pet off the guest sentinel).
 */
import { useAccountId } from './useAccountId';
import { getGuestFreePetId, getGuestWallet } from './guestPlay';

export interface WalletConnection {
  /**
   * True when the player has an authenticated identity (any chain) OR a connected Sui
   * wallet. Gate gameplay and identity-keyed backend actions on this — NOT on a Sui wallet.
   */
  isConnected: boolean;
  /**
   * The acting identity address (login identity, falling back to a connected-but-not-yet-
   * signed Sui wallet). Pass this to identity-keyed backend calls. Null only for a fully
   * logged-out state (or a wallet-less email-only account).
   */
  address: string | null;
  /** True only when a Sui wallet is connected in the browser (required for Sui-signing). */
  hasSuiWallet: boolean;
  /** The connected Sui wallet address. Use ONLY for Sui-signing / balance / gas / USDC. */
  suiAddress: string | null;
  /**
   * True when a free-pet marker exists in this browser AND no identity is connected.
   * A connected identity always wins over guest mode.
   */
  isGuest: boolean;
  /** The guest's sentinel wallet (`guest:<sessionId>`). Set only when `isGuest` is true. */
  guestWallet: string | null;
  /**
   * The address gameplay should act as: the authenticated/Sui identity if present,
   * otherwise the guest sentinel wallet, otherwise null. Prefer this over `address` at
   * call sites that also serve guests (gated on `isConnected || isGuest`).
   */
  actingAddress: string | null;
}

export function useWalletConnection(): WalletConnection {
  // Web2: identity is the Supabase account only. No wallet provider is mounted, so there is no
  // browser Sui wallet — `hasSuiWallet` is always false and the Sui-signing UI it gates
  // (withdraw, transfer, balances) never renders.
  const { address: identityAddress, isAuthenticated } = useAccountId();

  const freePetId = getGuestFreePetId();
  const isGuest = !isAuthenticated && Boolean(freePetId);
  const guestWallet = isGuest ? getGuestWallet() : null;

  return {
    isConnected: isAuthenticated,
    address: identityAddress,
    hasSuiWallet: false,
    suiAddress: null,
    isGuest,
    guestWallet,
    actingAddress: identityAddress ?? guestWallet,
  };
}
